import React, { useState, useEffect } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap"
import { FiClock } from "react-icons/fi"
import { Clock, Heart, Ticket, User, Calendar, CalendarPlus } from "lucide-react"
import axios from "axios"
import "./AgendaModal.css"
import { isMobile } from "react-device-detect"
import logoImg from "../../assets/images/logo512.png"

// ── ICS helpers ────────────────────────────────────────────────────────────────
// Escapes text per RFC 5545 so commas, semicolons, and newlines in session
// titles/descriptions don't break the .ics structure.
const escapeIcsText = (str = "") =>
  String(str)
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n")

// session.time / session.end are already adjusted to real-world instants by
// adjustToIst(), so formatting straight to UTC "Z" form here is correct
// regardless of the viewer's device timezone.
const fmtIcsDate = d => new Date(d).toISOString().replace(/-|:|\.\d+/g, "")

const generateSessionIcs = session => {
  const uid = `${session.icsUid || session._id || Date.now()}@walmart-converge-2026`
  const now = fmtIcsDate(new Date())

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Walmart Converge//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${fmtIcsDate(session.time)}`,
    `DTEND:${fmtIcsDate(session.end)}`,
    `SUMMARY:${escapeIcsText(session.title.replace(/\|/g, " - "))}`,
  ]

  if (session.description) {
    lines.push(`DESCRIPTION:${escapeIcsText(session.description)}`)
  }
  if (session.hall) {
    lines.push(`LOCATION:${escapeIcsText(session.hall)}`)
  }

  lines.push("END:VEVENT", "END:VCALENDAR")
  return lines.join("\r\n")
}

// ── Inline decorative background blobs ────────────────────────────────────────
const BackgroundBlobs = () => (
  <div
    aria-hidden="true"
    style={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    }}
  >
    {/* Top-right diagonal beam (matches reference image) */}
    <div
      style={{
        position: "absolute",
        top: "-80px",
        right: "-40px",
        width: "260px",
        height: "520px",
        background:
          "linear-gradient(135deg, transparent 0%, rgba(43,127,255,0.07) 30%, rgba(90,173,255,0.2) 50%, rgba(43,127,255,0.07) 70%, transparent 100%)",
        transform: "rotate(-18deg)",
        animation: "beamDrift 8s ease-in-out infinite alternate",
      }}
    />
    {/* Secondary thinner beam */}
    <div
      style={{
        position: "absolute",
        top: "0px",
        right: "80px",
        width: "120px",
        height: "400px",
        background:
          "linear-gradient(135deg, transparent 0%, rgba(127,212,255,0.12) 50%, transparent 100%)",
        transform: "rotate(-18deg)",
        animation: "beamDrift 8s ease-in-out infinite alternate",
        animationDelay: "1s",
      }}
    />
    {/* Bottom-left glow blob */}
    <div
      style={{
        position: "absolute",
        bottom: "-60px",
        left: "-60px",
        width: "280px",
        height: "280px",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(10,42,110,0.6) 0%, transparent 70%)",
      }}
    />
    {/* Spark dot — Walmart asterisk echo */}
    {/* <div
      style={{
        position: "absolute",
        top: "120px",
        right: "72px",
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        // background: "red",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color:"gold",
        fontSize: "20px",
        background:
          "radial-gradient(circle, rgba(255,194,32,0.85) 0%, transparent 70%)",
        boxShadow: "0 0 20px 6px rgba(255,194,32,0.35)",
        animation: "sparkPulse 3s ease-in-out infinite",
      }}
  ><img src={logoImg} style={{
    width: "150px", opacity: "0.5"
  }} alt=" " /></div> */}
  </div>
)

// ── Session Card ───────────────────────────────────────────────────────────────
const SessionCard = ({
  session,
  idx,
  obj,
  interestedSessions,
  bookIntrest,
  bookSession,
  addToCalendar,
  handleShowSpeaker,
  onDownloadIcs,
}) => (
  <div className="agenda-session" style={{ "--i": idx }}>
    <NavItem className="agenda-item">
      <NavLink className="agenda-link" style={{ position: "relative" }}>
        {/* Per-session "add to calendar" — icon-only, top-right of the card.
            Only shown when the session's data marks it as ICS-enabled. */}
        {session.showIcsButton && (
          <button
            type="button"
            className="agenda-session-ics-button"
            onClick={e => {
              e.stopPropagation()
              onDownloadIcs(session)
            }}
            aria-label="Add this session to calendar"
            title="Add to calendar"
          >
            <CalendarPlus size={16} />
          </button>
        )}

        {/* Title */}
        <div className="agenda-title">
          {session.title.split("|").map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < session.title.split("|").length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>

        {/* Time row */}
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}
        >
          <FiClock
            size={12}
            style={{
              color: "var(--converge-glow)",
              marginRight: "5px",
              flexShrink: 0,
            }}
          />
          <strong className="agenda-time">
            {new Date(session.time).toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </strong>
          <span
            style={{
              margin: "0 6px",
              color: "rgba(90,173,255,0.4)",
              fontSize: "11px",
            }}
          >
            —
          </span>
          <strong className="agenda-time">
            {new Date(session.end).toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </strong>
        </div>

        {/* Hall badge */}
        {/* {session.hall && (
          <div
            className="agenda-description"
            dangerouslySetInnerHTML={{
              __html: `<strong>Hall:</strong>&nbsp;${session.hall}`,
            }}
          />
        )} */}

        {/* Action buttons */}
        <div className="agenda-buttons">
          {session?.showIntrestedBtn && (
            <Button
              size="sm"
              color="Dark"
              className="agenda-button"
              id={`interested${session._id}`}
              onClick={() => bookIntrest(session.Secret, session._id)}
            >
              {interestedSessions.includes(session.Secret) ? (
                <Heart
                  className="agenda-button-icon"
                  fill="var(--converge-walmart)"
                  color="var(--converge-walmart)"
                  size={13}
                />
              ) : (
                <>
                  <Heart className="agenda-button-icon" size={13} />
                  Interested
                </>
              )}
            </Button>
          )}

          {/* {session?.booking && session?.showRsvpBtn && (
            <Button
              size="sm"
              color="Dark"
              className="agenda-button"
              id={session._id}
              disabled={session?.attendees?.includes(obj._id)}
              onClick={() =>
                bookSession(session.group, session._id, session.validPass)
              }
            >
              <Ticket className="agenda-button-icon" size={13} />
              {session?.attendees?.includes(obj._id) ? "Booked" : "Book"}
            </Button>
          )} */}

          {session?.speakersImg && (
            <Button
              size="sm"
              color="Dark"
              className="agenda-button"
              onClick={() => handleShowSpeaker(session.speakersImg)}
            >
              <User className="agenda-button-icon" size={13} />
              Speaker
            </Button>
          )}
        </div>
      </NavLink>
    </NavItem>
  </div>
)

// ── Main Component ─────────────────────────────────────────────────────────────
const AgendaModal = ({ isOpen, toggle }) => {
  const obj = JSON.parse(localStorage.getItem("authUser"))
  const [sessions, setSessions] = useState({})
  const [activeTab, setActiveTab] = useState("")
  const [showSpeaker, setShowSpeaker] = useState(false)
  const [speakerImg, setSpeakerImg] = useState(null)
  const [interestedSessions, setInterestedSessions] = useState(
    obj?.Intrested || []
  )

  const adjustToIst = utcDate => {
    utcDate = new Date(utcDate)
    return new Date(utcDate.getTime() - (5 * 60 + 30) * 60 * 1000)
  }

  const fetchAndStoreData = async () => {
    const adminPanel = isMobile
      ? `${process.env.REACT_APP_ADMIN}`
      : `${process.env.REACT_APP_ADMIN_LAPTOP}`
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"))
      const res = await fetch(`${adminPanel}/getUser/${authUser._id}`)
      const userData = await res.json()
      localStorage.setItem("authUser", JSON.stringify(userData))
    } catch (err) {
      console.error("Failed to fetch data:", err)
    }
  }

  useEffect(() => {
    let isMounted = true

    if (!isOpen) return

    const load = async () => {
      await fetchAndStoreData()
      axios
        .get(`${process.env.REACT_APP_ADMIN}/sessions/platformsession`)
        .then(response => {
          if (!response.data?.length) return
          const lowerTicket = obj.Ticket_Variant?.toLowerCase() || ""

          const grouped = response.data.reduce((acc, s) => {
            s.passType = s.passType?.toLowerCase() || ""
            acc[s.day] = acc[s.day] || []
            acc[s.day].push({
              _id: s._id,
              time: adjustToIst(s.time),
              end: adjustToIst(s.end),
              title: s.title,
              description: s.description,
              attendees: s.attendees,
              Secret: s.Secret,
              speaker: s.speaker,
              hall: s.hall,
              group: s.Group,
              showBookBtn: s.showBookBtn,
              showRsvpBtn: s.showRsvpBtn,
              showIntrestedBtn: s.showIntrestedBtn,
              booking: s.booking,
              vipBooking: s.vipBooking,
              speakersImg: s.speakersImg,
              validPass: true,
              // Stable UID source for the generated calendar event (optional —
              // falls back to _id if the sheet/API doesn't provide this field)
              icsUid: s.icsFile || "",
              // Data-driven toggle for the per-session "add to calendar" button,
              // sourced from the sheet's showIcsBtn column (1/0), same convention
              // as showRsvpBtn / showBookBtn / showIntrestedBtn.
              showIcsButton: !!Number(s.showIcsBtn),
            })
            return acc
          }, {})

          const sessionsData = {}
          let defaultKey
          Object.keys(grouped).forEach((key, i) => {
            if (i === 0) defaultKey = key
            sessionsData[key] = { name: key, data: grouped[key] }
          })

          if (isMounted) {
            setSessions(sessionsData)
            setActiveTab(defaultKey)
          }
        })
        .catch(err => console.error("Error fetching sessions:", err))
    }

    load()
    return () => {
      isMounted = false
    }
  }, [isOpen, obj.Ticket_Variant])

  const bookSession = (Group, sessionId, validPass) => {
    if (!validPass) {
      alert("Booking of this session is restricted")
      return
    }
    axios
      .post(`${process.env.REACT_APP_ADMIN}/sessions/${sessionId}/book`, {
        userId: obj?._id,
        Group,
      })
      .then(() => {
        alert(
          "Your session is booked - please show your QR code at the entry of this session"
        )
        const el = document.getElementById(sessionId)
        if (el) {
          el.classList.add("disabled")
          el.innerText = "Registered"
        }
      })
      .catch(err => alert(err?.response?.data?.error || "Booking failed"))
  }

  const bookIntrest = (title, sessionId) => {
    const isInterested = interestedSessions.includes(title)
    const url = `${process.env.REACT_APP_ADMIN}/sessions/${sessionId}/${
      isInterested ? "deintrest" : "intrest"
    }`
    const method = isInterested ? axios.patch : axios.post
    method(url, { userId: obj?._id, title })
      .then(() =>
        setInterestedSessions(prev =>
          isInterested ? prev.filter(i => i !== title) : [...prev, title]
        )
      )
      .catch(err => alert(err?.response?.data?.error || "Action failed"))
  }

  const addToCalendar = () => {
    const Time = "2026-09-30T02:30:00Z"  // 8:00 AM IST
    const end  = "2026-09-30T10:30:00Z"  // 4:30 PM IST
    const fmt  = d => new Date(d).toISOString().replace(/-|:|\.\d+/g, "")
    const ics  = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Walmart Converge//EN",
      "BEGIN:VEVENT",
      `URL:${document.URL}`,
      `DTSTART:${fmt(Time)}`,
      `DTEND:${fmt(end)}`,
      "SUMMARY:Walmart Converge 2026",
      "LOCATION:Online",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n")

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", "Walmart_Converge_2026.ics")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  // Per-session .ics — built on the spot from that session's own time/title/
  // hall/description, so there's nothing to keep in sync with a file folder.
  const handleDownloadSessionIcs = session => {
    try {
      const icsContent = generateSessionIcs(session)
      const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" })
      const safeName =
        (session.title || "session").replace(/[^\w\- ]/g, "").trim().slice(0, 60) || "session"

      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.setAttribute("download", `${safeName}.ics`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    } catch (err) {
      console.error("Failed to generate session ICS:", err)
      alert(`Couldn't create a calendar file for "${session.title}".`)
    }
  }

  const handleShowSpeaker = imgUrl => {
    setSpeakerImg(imgUrl)
    setShowSpeaker(true)
  }

  const days = Object.keys(sessions)

  return (
    <>
      {/* ── Keyframe injection ── */}
      <style>{`
        @keyframes beamDrift {
          from { opacity: 0.6; transform: rotate(-18deg) translateX(0); }
          to   { opacity: 1;   transform: rotate(-18deg) translateX(20px); }
        }
        @keyframes sparkPulse {
          0%,100% { opacity:0.8; transform:scale(1); }
          50%      { opacity:0.4; transform:scale(0.7); }
        }
        @keyframes sessionFadeIn {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .agenda-session { animation: sessionFadeIn 0.4s ease both; animation-delay: calc(var(--i,0)*0.055s); }
      `}</style>

      {/* ── Speaker sub-modal ── */}
      {showSpeaker && (
        <Modal
          isOpen={showSpeaker}
          toggle={() => setShowSpeaker(false)}
          centered
          style={{ width: "auto" }}
        >
          <div
            className="modal-header"
            style={{
              background: "var(--converge-navy, #061330)",
              borderBottom: "1px solid rgba(90,173,255,0.18)",
            }}
          >
            <h5
              className="modal-title mt-0"
              style={{
                // fontFamily: "'Rajdhani', sans-serif",
                fontSize: "22px",
                fontWeight: 700,
                letterSpacing: "3px",
                textTransform: "uppercase",
                background: "linear-gradient(90deg, #e8f4ff, #7fd4ff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Speaker
            </h5>
            <button
              type="button"
              onClick={() => setShowSpeaker(false)}
              className="close"
              aria-label="Close"
              style={{
                color: "#5aadff",
                background: "none",
                border: "none",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                textShadow: "0 0 8px rgba(43,127,255,0.6)",
                marginRight: "4%",
              }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div
            className="modal-body text-center"
            style={{
              background: "var(--converge-navy, #061330)",
              padding: "20px",
            }}
          >
            {speakerImg && (
              <img
                src={speakerImg}
                alt="Speaker"
                style={{ maxWidth: "100%", borderRadius: "10px" }}
              />
            )}
          </div>
        </Modal>
      )}

      {/* ── Main agenda modal ── */}
      <Modal isOpen={isOpen} toggle={toggle} className="agenda-modal">
        <div
          style={{
            position: "relative",
            background:
              "linear-gradient(175deg, rgb(6, 19, 48) 0%, rgba(2, 11, 26, 0.24) 45%, rgba(0, 0, 0, 0.76) 100%)",
            minHeight: "100vh",
            overflow: "hidden",
          }}
        >
          <BackgroundBlobs />

          {/* Header */}
          <div
            style={{
              position: "relative",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 20px 14px",
              borderBottom: "1px solid rgba(90,173,255,0.15)",
            }}
          >
            <span
              style={{
                // fontFamily: "'Rajdhani', sans-serif",
                fontSize: "22px",
                fontWeight: 700,
                letterSpacing: "3px",
                textTransform: "uppercase",
                background: "linear-gradient(90deg, #e8f4ff, #7fd4ff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Agenda
            </span>
            <button
              onClick={toggle}
              style={{
                background: "none",
                border: "none",
                color: "var(--converge-walmart)",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                textShadow: "0 0 8px rgba(43,127,255,0.6)",
                padding: "0 2px",
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div
            className="agenda-body"
            style={{ position: "relative", zIndex: 5 }}
          >
            {/* Day tabs */}
            {days.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  padding: "0 16px",
                  borderBottom: "1px solid rgba(90,173,255,0.12)",
                }}
              >
                {days.map(day => (
                  <button
                    key={day}
                    onClick={() => setActiveTab(day)}
                    style={{
                      background: "none",
                      border: "none",
                      borderBottom: `2px solid ${
                        activeTab === day
                          ? "var(--converge-glow, #2b7fff)"
                          : "transparent"
                      }`,
                      color:
                        activeTab === day
                          ? "var(--converge-cyan, #7fd4ff)"
                          : "rgba(90,173,255,0.5)",
                      // fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      padding: "12px 16px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      textShadow:
                        activeTab === day
                          ? "0 0 10px rgba(127,212,255,0.4)"
                          : "none",
                    }}
                  >
                    {sessions[day].name}
                  </button>
                ))}
              </div>
            )}

            {/* Session list */}
            <div style={{ padding: "0 16px" }}>
              {/* Add to Calendar */}
              <button
                className="agenda-button"
                onClick={addToCalendar}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  margin: "16px 0 6px",
                  padding: "12px 20px",
                  fontSize: "13px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background:
                    "linear-gradient(135deg, rgba(43,127,255,0.18), rgba(10,42,110,0.5))",
                  border: "1px solid rgba(90,173,255,0.35)",
                  borderRadius: "8px",
                  color: "#5aadff",
                  cursor: "pointer",
                  backdropFilter: "blur(6px)",
                  fontFamily: "'Exo 2', sans-serif",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "rgba(90,173,255,0.7)"
                  e.currentTarget.style.boxShadow =
                    "0 0 18px rgba(43,127,255,0.3)"
                  e.currentTarget.style.color = "#fff"
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "rgba(90,173,255,0.35)"
                  e.currentTarget.style.boxShadow = "none"
                  e.currentTarget.style.color = "#5aadff"
                }}
              >
                <Calendar size={16} />
                Add to Calendar
              </button>

              {/* Sessions */}
              {sessions[activeTab]?.data?.map((session, idx) => (
                <SessionCard
                  key={session._id || idx}
                  session={session}
                  idx={idx}
                  obj={obj}
                  interestedSessions={interestedSessions}
                  bookIntrest={bookIntrest}
                  bookSession={bookSession}
                  addToCalendar={addToCalendar}
                  handleShowSpeaker={handleShowSpeaker}
                  onDownloadIcs={handleDownloadSessionIcs}
                />
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AgendaModal